import React from "react";
import { useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES } from "../assets/asset";

const sectionClass = "bg-white/70 rounded-xl p-4 md:p-5 shadow-sm";
const headingClass = "text-[#0B2B66] font-bold text-xl md:text-2xl mb-2";
const textClass = "text-gray-800 text-sm md:text-base leading-relaxed";

const SubscriptionTermsPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen font-mali overflow-y-auto px-4 pt-2 pb-4 md:pt-4 md:pb-6"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg w-full max-w-5xl mx-auto px-4 pt-4 pb-6 sm:px-6 md:px-8 relative">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="absolute top-3 right-3 text-xs md:text-sm font-bold px-3 py-1.5 rounded-md border border-[#0B2B66] text-[#0B2B66] bg-white/90 hover:bg-white"
        >
          Back
        </button>

        <h1 className="text-3xl md:text-4xl font-bold text-[#0B2B66] mb-1">
          Terms of Use MathDingo
        </h1>
        <p className="text-sm md:text-base text-gray-700 mb-6">
          Effective Date: 1 January 2025
        </p>
        <p className="text-sm md:text-base text-gray-700 mb-6">
          Product of Byte Digital Consulting Pty Ltd, Melbourne, Australia
        </p>

        <div className="space-y-4">
          <section className={sectionClass}>
            <h2 className={headingClass}>1. Acceptance of Terms</h2>
            <p className={textClass}>
              By accessing, downloading, installing, or using the MathDingo iPad
              App or Web Application ("MathDingo", "App", or "Platform"), you
              agree to comply with and be bound by these Terms of Use
              ("Terms"). If you do not agree to these Terms, you must not use
              MathDingo.
            </p>
            <p className={`${textClass} mt-2`}>
              These Terms form a legally binding agreement between you and Byte
              Digital Consulting Pty Ltd ("we", "us", "our"). We may modify
              these Terms at any time. Continued use of MathDingo after changes
              are made constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>2. Eligibility</h2>
            <p className={textClass}>MathDingo is intended for use by:</p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>Residents of Australia, and</li>
              <li>
                Users aged 3 years and older (with parental or guardian consent
                where required).
              </li>
            </ul>
            <p className={`${textClass} mt-2`}>
              By using MathDingo, you confirm that you meet these eligibility
              requirements.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>3. Subscription and Payment Terms</h2>
            <p className={textClass}>
              MathDingo operates on a subscription-based model. The pricing
              (AUD) is:
            </p>
            <div className="overflow-x-auto mt-3">
              <table className="w-full border border-gray-300 rounded-lg text-sm md:text-base">
                <thead>
                  <tr className="bg-[#EAF3FF] text-[#0B2B66]">
                    <th className="text-left px-3 py-2 border-b border-gray-300">
                      Plan
                    </th>
                    <th className="text-left px-3 py-2 border-b border-gray-300">
                      Monthly
                    </th>
                    <th className="text-left px-3 py-2 border-b border-gray-300">
                      Annual
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  <tr>
                    <td className="px-3 py-2 border-b border-gray-200">Basic</td>
                    <td className="px-3 py-2 border-b border-gray-200">$4.99</td>
                    <td className="px-3 py-2 border-b border-gray-200">$59.99</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Pro</td>
                    <td className="px-3 py-2">$9.99</td>
                    <td className="px-3 py-2">$119.99</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={`${textClass} mt-3`}>
              Subscriptions are processed via Apple App Store, Google Play
              Store, Stripe, or other third-party payment providers (for web
              users).
            </p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>
                You authorise recurring billing unless you cancel before the
                next billing cycle.
              </li>
              <li>
                Subscriptions automatically renew unless cancelled through your
                account settings.
              </li>
              <li>
                Refunds are governed by applicable consumer laws and the
                respective platform's refund policies.
              </li>
              <li>We reserve the right to adjust pricing with reasonable notice.</li>
            </ul>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>4. Licence to Use</h2>
            <p className={textClass}>
              Subject to compliance with these Terms, we grant you a limited,
              non-exclusive, revocable, non-transferable licence to use
              MathDingo for personal, non-commercial educational purposes.
            </p>
            <p className={`${textClass} mt-2`}>You must not:</p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>Reproduce, redistribute, or resell any part of MathDingo.</li>
              <li>
                Use the service in a commercial setting (e.g., paid tutoring)
                unless otherwise licensed.
              </li>
              <li>
                Circumvent access controls or reverse-engineer any part of the
                app or web platform.
              </li>
            </ul>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>5. User Conduct</h2>
            <p className={textClass}>You agree not to:</p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>
                Interfere with the functioning of MathDingo or disrupt other
                users.
              </li>
              <li>Use the app to harass, abuse, or harm any other user.</li>
              <li>
                Upload, distribute, or publish content that is offensive,
                illegal, or infringes on intellectual property rights.
              </li>
            </ul>
            <p className={`${textClass} mt-2`}>
              Parental supervision is recommended for users under 13.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>6. Intellectual Property</h2>
            <p className={textClass}>
              All content, software, lesson materials, graphics, characters,
              animations, and branding elements are the intellectual property of
              Byte Digital Consulting Pty Ltd or its licensors.
            </p>
            <p className={`${textClass} mt-2`}>
              You may not use our trademarks, logos, or proprietary content
              without express written permission.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>7. Privacy and Data Use</h2>
            <p className={textClass}>
              Your use of MathDingo is subject to our Privacy Policy (available
              in-app and online), which details:
            </p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>What data we collect (e.g., name, usage data, progress),</li>
              <li>
                How it's stored (in compliance with Australian Privacy
                Principles and GDPR where applicable),
              </li>
              <li>Who it's shared with (only trusted providers),</li>
              <li>
                Your rights to access, correct, or delete your information.
              </li>
            </ul>
            <p className={`${textClass} mt-2`}>
              For school or tutor accounts, we comply with child data privacy
              standards and offer data processing agreements (DPA) upon request.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>8. Platform Availability and Updates</h2>
            <p className={textClass}>We reserve the right to:</p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>Modify, update, or discontinue any feature without notice.</li>
              <li>
                Perform maintenance or platform upgrades that may temporarily
                affect access.
              </li>
            </ul>
            <p className={`${textClass} mt-2`}>
              We aim to ensure high uptime but cannot guarantee uninterrupted
              service.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>9. Termination of Use</h2>
            <p className={textClass}>
              We may suspend or terminate your access without notice if you:
            </p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>Violate these Terms,</li>
              <li>
                Use the platform in a fraudulent, abusive, or unlawful manner,
              </li>
              <li>Interfere with the experience of other users.</li>
            </ul>
            <p className={`${textClass} mt-2`}>
              Upon termination, your licence is revoked, and any data
              associated with your account may be deleted in accordance with our
              retention policy.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>
              10. Disclaimers and Limitation of Liability
            </h2>
            <p className={textClass}>
              To the maximum extent permitted under Australian law:
            </p>
            <ul className={`${textClass} list-disc list-inside mt-2 space-y-1`}>
              <li>
                MathDingo is provided "as is" and "as available" with no
                warranties of fitness for purpose or error-free performance.
              </li>
              <li>
                We are not liable for any indirect, incidental, or consequential
                damages arising from your use of the App.
              </li>
              <li>
                We do not guarantee specific educational outcomes or results.
              </li>
            </ul>
            <p className={`${textClass} mt-2`}>
              Nothing in these Terms excludes your consumer rights under the
              Australian Consumer Law.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>11. Governing Law and Disputes</h2>
            <p className={textClass}>
              These Terms are governed by the laws of Victoria, Australia. Any
              disputes shall be resolved exclusively in the courts of Victoria.
            </p>
            <p className={`${textClass} mt-2`}>
              If a provision of these Terms is found invalid, the remainder
              shall remain in effect.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className={headingClass}>12. Contact Information</h2>
            <p className={textClass}>
              If you have any questions or concerns about these Terms, please
              contact us at:
            </p>
            <div className={`${textClass} mt-2`}>
              <p>Email: support@mathdingo.com</p>
              <p>Email: mathdingo.support@byte-digital.com.au</p>
              <p className="mt-2">
                Address: Byte Digital Australia, Level 30/35 Collins St,
                Melbourne VIC 3000, Australia
              </p>
            </div>
          </section>
        </div>

        <section className="relative overflow-hidden mt-6 rounded-2xl border-4 border-[#0B2B66] bg-gradient-to-br from-[#FFE066] via-[#FFB347] to-[#FF7AA2] shadow-[0_8px_0_#0B2B66] px-5 py-5 md:px-6 md:py-6">
          <div className="absolute -top-2 -left-1 h-5 w-5 rounded-full bg-white/80" />
          <div className="absolute top-2 right-4 h-3 w-3 rounded-full bg-white/80" />
          <div className="absolute bottom-3 right-10 h-4 w-4 rounded-full bg-white/60" />

          <h3 className="text-[#0B2B66] font-extrabold text-2xl md:text-3xl tracking-wide">
            Acknowledgement
          </h3>
          <p className="mt-2 text-[#0B2B66] font-bold text-sm md:text-lg leading-relaxed">
            By using MathDingo, you acknowledge that you have read, understood,
            and agree to these Terms of Use.
          </p>
        </section>
      </div>
    </div>
  );
};

export default SubscriptionTermsPage;
